import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash, FaList, FaColumns } from "react-icons/fa";
import TaskModal from "./taskmodal";
import ViewTask from "./viewtask";
import KanbanBoard from "./kanban_board";
import ConfirmModal from "../employee/delete_modal";
import Dropdown from "../../components/modal/dropdown";
import { toast } from "react-toastify";
import { getTasks, getMyTasks, createTask, updateTask, deleteTask, changeTaskStatus, getTaskById, uploadTaskAttachment } from "../services/taskservice";
import { TaskStatus, TaskPriority } from "../../constants/constants";
import { useAuth } from "../context/authcontext";
import Can from "../common/can";
import Pagination from "../common/pagination";
import "./task.css";

const PAGE_SIZE = 20;

const Task = () => {
  const { hasPermission, hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["task.view", "task.update", "task.delete"]);
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [totalCount, setTotal]    = useState(0);

  const [openModal, setOpenModal]     = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewTask, setViewTask]       = useState(null);
  const [viewMode, setViewMode]       = useState("kanban"); // "list" | "kanban"

  const [search, setSearch]               = useState("");
  const [statusFilter, setStatusFilter]   = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("status") || "All";
  });
  const [priorityFilter, setPriorityFilter] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("priority") || "All";
  });

  const canViewAll = hasPermission("task.view");
  const canCreate = hasPermission("task.create");

  // Guards against out-of-order fetch responses (e.g. an unfiltered request
  // that resolves after a filtered one and overwrites it with stale data)
  const fetchIdRef = useRef(0);

  const fetchTasks = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search)                       params.search   = search;
      const statusMap = {
        "To-Do": TaskStatus.TODO,
        "In Progress": TaskStatus.IN_PROGRESS,
        "Done": TaskStatus.DONE,
        "On Hold": TaskStatus.ON_HOLD,
        "Cancelled": TaskStatus.CANCELLED
      };
      const priorityMap = {
        "Low": TaskPriority.LOW,
        "Medium": TaskPriority.MEDIUM,
        "High": TaskPriority.HIGH,
        "Urgent": TaskPriority.URGENT
      };
      if (statusFilter !== "All")       params.status   = statusMap[statusFilter];
      if (priorityFilter !== "All")     params.priority = priorityMap[priorityFilter];
      
      const fetchFn = canViewAll ? getTasks : getMyTasks;
      const res = await fetchFn(params);
      if (fetchId !== fetchIdRef.current) return; // a newer request already superseded this one
      
      const data = res.data || res;
      const meta = res.meta || {};
      setTasks(Array.isArray(data) ? data : data.results || []);
      setTotal(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch {
      if (fetchId === fetchIdRef.current) toast.error("Failed to load tasks");
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [page, search, statusFilter, priorityFilter, canViewAll]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { setPage(1); }, [search, statusFilter, priorityFilter]);

  // Handle direct link to task
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get("taskId");
    if (taskId) {
      getTaskById(taskId)
        .then(res => setViewTask(res.data || res))
        .catch(() => toast.error("Failed to load task details"));
      
      // Clean up URL without reloading
      navigate("/task", { replace: true });
    }
  }, [location.search, navigate]);

  /* Pick up ?priority= / ?status= query params (e.g. from Dashboard quick-links)
     and pre-apply them to the filter dropdowns. Must match the Dropdown options:
     priority -> Low, Medium, High
     status   -> To-Do, In-Progress, Done, On-Hold, Cancelled */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const priority = params.get("priority");
    const status = params.get("status");
    if (priority) setPriorityFilter(priority);
    if (status) setStatusFilter(status);
  }, [location.search]);

  const handleSaveTask = async (taskData) => {
    try {
      const { attachments, ...rest } = taskData;
      let savedTask;
      if (selectedTask) {
        savedTask = await updateTask(selectedTask.id, rest);
        toast.success("Task updated successfully");
      } else {
        savedTask = await createTask(rest);
        toast.success("Task created successfully");
      }

      const taskId = selectedTask ? selectedTask.id : (savedTask.data ? savedTask.data.id : savedTask.id);
      
      if (attachments && attachments.length > 0) {
        for (let file of attachments) {
          const formData = new FormData();
          formData.append("file", file);
          await uploadTaskAttachment(taskId, formData);
        }
      }

      await fetchTasks();
      setSelectedTask(null);
      setOpenModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save task");
      throw err;
    }
  };

  const confirmDelete = async (password) => {
    try {
      await deleteTask(deleteTarget.id, password);
      toast.success("Task deleted successfully");
      await fetchTasks();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || "Failed to delete task: Incorrect password or permission denied";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await changeTaskStatus(task.id, { status: newStatus });
      toast.success("Status updated");
      await fetchTasks();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const getPriorityClass = (p) => {
    if (p === TaskPriority.LOW) return "low";
    if (p === TaskPriority.MEDIUM) return "medium";
    if (p === TaskPriority.HIGH) return "high";
    return "urgent";
  };
  const getStatusClass = (s) => {
    if (s === TaskStatus.TODO) return "todo";
    if (s === TaskStatus.IN_PROGRESS) return "in-progress";
    if (s === TaskStatus.DONE) return "done";
    if (s === TaskStatus.ON_HOLD) return "on-hold";
    return "cancelled";
  };
  const priorityBadge = (t) => <span className={`emp-status ${getPriorityClass(t.priority)}`}>{t.priority_display || t.priority}</span>;
  const statusBadge   = (t) => <span className={`emp-status ${getStatusClass(t.status)}`}>{t.status_display || t.status}</span>;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="container">
      <div className="page-header">
        <h2>{canViewAll ? "Tasks" : "My Tasks"}</h2>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ display: "flex", background: "var(--bg-main)", border: "1px solid var(--border-color)", padding: "4px", borderRadius: "10px" }}>
            <button 
              style={{ background: viewMode === "list" ? "var(--bg-card)" : "transparent", border: "none", padding: "6px 14px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", color: viewMode === "list" ? "var(--primary)" : "var(--text-muted)", fontWeight: "600", boxShadow: viewMode === "list" ? "var(--shadow-sm)" : "none", transition: "all 0.2s" }}
              onClick={() => setViewMode("list")}
            >
              <FaList /> List
            </button>
            <button 
              style={{ background: viewMode === "kanban" ? "var(--bg-card)" : "transparent", border: "none", padding: "6px 14px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", color: viewMode === "kanban" ? "var(--primary)" : "var(--text-muted)", fontWeight: "600", boxShadow: viewMode === "kanban" ? "var(--shadow-sm)" : "none", transition: "all 0.2s" }}
              onClick={() => setViewMode("kanban")}
            >
              <FaColumns /> Board
            </button>
          </div>
          {canCreate && (
            <button className="primary-btn" onClick={() => { setSelectedTask(null); setOpenModal(true); }}>
              Add New Task
            </button>
          )}
        </div>
      </div>

      <div className="filters-card single">
        <input
          className="search-input"
          placeholder="🔍 Search task or assignee..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Dropdown
          options={["All", "To-Do", "In-Progress", "Done", "On-Hold", "Cancelled"]}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="Status"
        />
        <Dropdown
          options={["All", "Low", "Medium", "High"]}
          value={priorityFilter}
          onChange={setPriorityFilter}
          placeholder="Priority"
        />
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading tasks…</p></div>
      ) : viewMode === "kanban" ? (
        <KanbanBoard 
          tasks={tasks}
          onStatusChange={handleStatusChange}
          onViewTask={setViewTask}
          onEditTask={(t) => { setSelectedTask(t); setOpenModal(true); }}
          onDeleteTask={setDeleteTarget}
        />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Task ID</th>
              <th>Title</th>
              <th>Assigned To</th>
              <th>Project</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Due Date</th>
              {canShowActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr><td colSpan={canShowActions ? "8" : "7"}>
                <div className="empty-state">
                  <img src="/images/tasks.svg" alt="No tasks" className="empty-img" />
                  <p>No Tasks Found</p>
                </div>
              </td></tr>
            ) : (
              tasks.map((t) => (
                <tr key={t.id}>
                  <td>{t.task_id}</td>
                  <td>{t.title}</td>
                  <td>{t.assigned_to_name || t.team_name || "—"}</td>
                  <td>{t.project_name || "—"}</td>
                  <td>{priorityBadge(t)}</td>
                  <td>{statusBadge(t)}</td>
                  <td>{t.due_date || "—"}</td>
                  {canShowActions && (
                    <td className="action-icons">
                      <Can perm="task.view">
                        <button className="icon-btn view" onClick={() => setViewTask(t)}><FaEye /></button>
                      </Can>
                      <Can perm="task.update">
                        <button className="icon-btn edit" onClick={() => { setSelectedTask(t); setOpenModal(true); }}><FaEdit /></button>
                      </Can>
                      <Can perm="task.delete">
                        <button className="icon-btn delete" onClick={() => setDeleteTarget(t)}><FaTrash /></button>
                      </Can>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {viewTask && (
        <ViewTask
          task={viewTask}
          onClose={() => setViewTask(null)}
          onEdit={() => { setSelectedTask(viewTask); setOpenModal(true); setViewTask(null); }}
          onStatusChange={handleStatusChange}
        />
      )}

      {openModal && (
        <TaskModal
          isEdit={!!selectedTask}
          existing={selectedTask}
          onSave={handleSaveTask}
          onClose={() => { setOpenModal(false); setSelectedTask(null); }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Task"
          message={`Delete "${deleteTarget.title}"?`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default Task;